import { useCallback, useEffect, useMemo, useState } from 'react';
import { Toast } from 'antd-mobile';
import i18n from '../../../i18n';
import { campaignApi } from '../../../api/campaigns';
import { ApiEnvelope } from '../../../api/types';
import { MyWhatsAppAccountBinding, whatsappApi } from '../../../api/whatsapp';
import { CampaignDetailState, CampaignSendBatchItem, CampaignSendReceiptItem, CampaignTemplateCard } from './types';

function readErrorMessage(error: { response?: { data?: { error?: { message?: string } } } }, fallback: string): string {
  return error.response?.data?.error?.message || fallback;
}

export function useCampaignDetailModel(campaignId: string | undefined) {
  const [state, setState] = useState<CampaignDetailState>({
    loading: true,
    campaign: null,
    accountBindings: [],
    accountBindingsLoading: true,
    selectedBindingId: null,
    connectionState: null,
    connectionStateLoading: true,
    connectionStateError: '',
    forwardOpen: false,
    forwardContent: '',
    forwardMediaUrl: undefined,
    sendBatches: [],
    sendBatchesLoading: true,
    cancellingBatchId: null,
    sendReceipts: [],
    sendReceiptsLoading: true,
  });

  const selectedBinding = useMemo(() => (
    state.accountBindings.find((binding) => binding.id === state.selectedBindingId)
    || state.accountBindings.find((binding) => binding.is_default)
    || state.accountBindings[0]
    || null
  ), [state.accountBindings, state.selectedBindingId]);

  const selectedWaAccountId = selectedBinding?.wa_account_id;

  const loadBindings = useCallback(async () => {
    setState((previous) => ({ ...previous, accountBindingsLoading: true }));

    try {
      const response = await whatsappApi.getMyAccounts() as { data: ApiEnvelope<MyWhatsAppAccountBinding[]> };
      const bindings = response.data.data || [];

      setState((previous) => ({
        ...previous,
        accountBindings: bindings,
        accountBindingsLoading: false,
        selectedBindingId: bindings.some((binding) => binding.id === previous.selectedBindingId)
          ? previous.selectedBindingId
          : (bindings.find((binding) => binding.is_default)?.id || bindings[0]?.id || null),
      }));
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(
          error as { response?: { data?: { error?: { message?: string } } } },
          i18n.t('campaigns:detail.loadBindingsFailed')
        ),
      });
      setState((previous) => ({
        ...previous,
        accountBindingsLoading: false,
        accountBindings: [],
        selectedBindingId: null,
      }));
    }
  }, []);

  const loadCampaign = useCallback(async (waAccountId?: string) => {
    if (!campaignId) {
      setState((previous) => ({ ...previous, loading: false }));
      return;
    }

    setState((previous) => ({ ...previous, loading: true }));

    try {
      const campaignResponse = await campaignApi.get(campaignId, waAccountId ? { wa_account_id: waAccountId } : undefined);

      setState((previous) => ({
        ...previous,
        loading: false,
        campaign: campaignResponse.data.data,
      }));
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as { response?: { data?: { error?: { message?: string } } } }, i18n.t('campaigns:detail.loadFailed')),
      });
      setState((previous) => ({ ...previous, loading: false }));
    }
  }, [campaignId]);

  const loadSendReceipts = useCallback(async (silent = false, waAccountId?: string) => {
    if (!campaignId) {
      setState((previous) => ({ ...previous, sendReceiptsLoading: false, sendReceipts: [] }));
      return;
    }

    if (!silent) {
      setState((previous) => ({ ...previous, sendReceiptsLoading: true }));
    }

    try {
      const response = await whatsappApi.getMySendReceipts({
        campaign_id: campaignId,
        wa_account_id: waAccountId,
        limit: 8,
      });
      setState((previous) => ({
        ...previous,
        sendReceiptsLoading: false,
        sendReceipts: (response.data.data || []) as CampaignSendReceiptItem[],
      }));
    } catch (error) {
      setState((previous) => ({ ...previous, sendReceiptsLoading: false }));
      if (!silent) {
        Toast.show({
          icon: 'fail',
          content: readErrorMessage(error as { response?: { data?: { error?: { message?: string } } } }, i18n.t('campaigns:detail.loadReceiptsFailed')),
        });
      }
    }
  }, [campaignId]);

  const loadSendBatches = useCallback(async (silent = false, waAccountId?: string) => {
    if (!campaignId) {
      setState((previous) => ({ ...previous, sendBatchesLoading: false, sendBatches: [] }));
      return;
    }

    if (!silent) {
      setState((previous) => ({ ...previous, sendBatchesLoading: true }));
    }

    try {
      const response = await campaignApi.listSendBatches(campaignId, {
        wa_account_id: waAccountId,
        limit: 5,
      });
      setState((previous) => ({
        ...previous,
        sendBatchesLoading: false,
        sendBatches: (response.data.data || []) as CampaignSendBatchItem[],
      }));
    } catch (error) {
      setState((previous) => ({ ...previous, sendBatchesLoading: false }));
      if (!silent) {
        Toast.show({
          icon: 'fail',
          content: readErrorMessage(error as { response?: { data?: { error?: { message?: string } } } }, i18n.t('campaigns:detail.loadBatchesFailed')),
        });
      }
    }
  }, [campaignId]);

  const loadConnectionState = useCallback(async (waAccountId?: string, silent = false) => {
    if (!waAccountId) {
      setState((previous) => ({
        ...previous,
        connectionState: null,
        connectionStateLoading: false,
        connectionStateError: '',
      }));
      return;
    }

    if (!silent) {
      setState((previous) => ({
        ...previous,
        connectionStateLoading: true,
        connectionStateError: '',
      }));
    }

    try {
      const response = await whatsappApi.getConnectionState(waAccountId);
      setState((previous) => ({
        ...previous,
        connectionState: response.data.data,
        connectionStateLoading: false,
        connectionStateError: '',
      }));
    } catch (error) {
      const message = readErrorMessage(
        error as { response?: { data?: { error?: { message?: string } } } },
        i18n.t('campaigns:detail.loadConnectionStateFailed')
      );
      setState((previous) => ({
        ...previous,
        connectionState: null,
        connectionStateLoading: false,
        connectionStateError: message,
      }));
      if (!silent) {
        Toast.show({
          icon: 'fail',
          content: message,
        });
      }
    }
  }, []);

  useEffect(() => {
    void loadBindings();
  }, [loadBindings]);

  useEffect(() => {
    void loadCampaign(selectedWaAccountId);
  }, [loadCampaign, selectedWaAccountId]);

  useEffect(() => {
    void loadSendBatches(false, selectedWaAccountId);
    void loadSendReceipts(false, selectedWaAccountId);
    void loadConnectionState(selectedWaAccountId);

    const timer = window.setInterval(() => {
      void loadSendBatches(true, selectedWaAccountId);
      void loadSendReceipts(true, selectedWaAccountId);
      void loadConnectionState(selectedWaAccountId, true);
    }, 4000);

    return () => {
      window.clearInterval(timer);
    };
  }, [loadConnectionState, loadSendBatches, loadSendReceipts, selectedWaAccountId]);

  const defaultForwardContent = useMemo(() => {
    const campaign = state.campaign;
    if (!campaign) {
      return '';
    }

    const parts = [campaign.name];
    if (campaign.description) {
      parts.push(campaign.description);
    }
    if (campaign.destination_url) {
      parts.push(campaign.destination_url);
    }
    return parts.join('\n\n');
  }, [state.campaign]);

  const templates = useMemo<CampaignTemplateCard[]>(() => {
    return (state.campaign?.content_templates || []).map((template, index) => ({
      key: `${index}`,
      title: i18n.t('campaigns:detail.templateTitle', { index: index + 1 }),
      content: buildTemplateMessage(template.text, template.promo_link || state.campaign?.destination_url),
      mediaUrl: template.media_url,
      rawTemplate: template,
    }));
  }, [state.campaign]);

  const openForward = useCallback((content: string, mediaUrl?: string) => {
    setState((previous) => ({
      ...previous,
      forwardOpen: true,
      forwardContent: content,
      forwardMediaUrl: mediaUrl,
    }));
  }, []);

  const closeForward = useCallback(() => {
    setState((previous) => ({ ...previous, forwardOpen: false, forwardMediaUrl: undefined }));
  }, []);

  const handleForwardSubmitted = useCallback(() => {
    void loadCampaign(selectedWaAccountId);
    void loadSendBatches(true, selectedWaAccountId);
    void loadSendReceipts(true, selectedWaAccountId);
  }, [loadCampaign, loadSendBatches, loadSendReceipts, selectedWaAccountId]);

  const cancelSendBatch = useCallback(async (batchId: string) => {
    if (!campaignId) {
      return;
    }

    setState((previous) => ({ ...previous, cancellingBatchId: batchId }));
    try {
      await campaignApi.cancelSendBatch(campaignId, batchId);
      Toast.show({
        icon: 'success',
        content: i18n.t('campaigns:detail.batchCancelSuccess'),
      });
      await Promise.all([
        loadSendBatches(true, selectedWaAccountId),
        loadSendReceipts(true, selectedWaAccountId),
        loadCampaign(selectedWaAccountId),
      ]);
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as { response?: { data?: { error?: { message?: string } } } }, i18n.t('campaigns:detail.batchCancelFailed')),
      });
    } finally {
      setState((previous) => ({ ...previous, cancellingBatchId: null }));
    }
  }, [campaignId, loadCampaign, loadSendBatches, loadSendReceipts, selectedWaAccountId]);

  const selectBinding = useCallback((bindingId: string) => {
    setState((previous) => ({
      ...previous,
      selectedBindingId: bindingId,
    }));
  }, []);

  return {
    state,
    templates,
    selectedBinding,
    defaultForwardContent,
    reload: () => loadCampaign(selectedWaAccountId),
    reloadSendReceipts: loadSendReceipts,
    openForward,
    closeForward,
    handleForwardSubmitted,
    selectBinding,
    cancelSendBatch,
  };
}

function buildTemplateMessage(text: string, promoLink?: string | null): string {
  if (!promoLink) {
    return text;
  }

  if (text.includes('{{link}}')) {
    return text.replace(/\{\{link\}\}/gi, promoLink);
  }

  if (text.includes(promoLink)) {
    return text;
  }

  return `${text}\n\n${promoLink}`;
}
